import db from "../db/database";

import { markForDelete, markForSync } from "./syncService";
import { buscarValorEnUso, buscarValorEnUsoAlEditar } from "./unicidad";

/**
 * Normaliza un código de tienda: mayúsculas y sin espacios sobrantes en los
 * extremos, pero CONSERVANDO los interiores.
 *
 * Los códigos reales del ambiente los llevan -- "MALL DEL SOL" es un
 * `Cod. Tienda` legítimo, junto a "PVLAY" y "URDESA" -- a diferencia de los de
 * caja ("PVMDSC1") y los de cajero ("C1MDS"), que nunca tienen espacios. Quitar
 * los interiores convertía "MALL DEL SOL" en "MALLDELSOL", que no coincide con
 * nada: por eso una tienda repetida pasaba la validación y se daba de alta dos
 * veces.
 */
export const normalizarCodigoTienda = (valor) => (valor || "").toUpperCase().trim();

// Servicio para manejar las operaciones relacionadas con los TPVs

// Agregar un nuevo TPV a la base de datos
export const addTPV = async (tpv) => {
  try {
    const nombreEnUso = await buscarValorEnUso(db.tpvs, "nombre", tpv.nombre);

    if (nombreEnUso) {
      throw new Error(`Ya existe una tienda con ese nombre: ${nombreEnUso.nombre}`);
    }

    // El código es la clave de la tienda en Business Central, así que un
    // duplicado no falla: TryProcesarTienda encuentra la tienda existente y
    // entra por la rama de actualización, sobrescribiendo sus datos en silencio.
    // Se corta acá, donde todavía se puede explicar cuál es el choque.
    //
    // Un código vacío es válido: significa que lo asigna BC de la serie.
    const codigo = normalizarCodigoTienda(tpv.No);

    if (codigo) {
      // Se compara normalizando los dos lados en vez de con un igual exacto
      // sobre el índice: así "mall del sol", "MALL DEL SOL " y "MALL DEL SOL"
      // se reconocen como el mismo código. Son pocas tiendas, así que recorrer
      // la tabla no cuesta nada.
      const codigoEnUso = (await db.tpvs.toArray()).find(
        (otra) => !otra.deleted && normalizarCodigoTienda(otra.No) === codigo
      );

      if (codigoEnUso) {
        throw new Error(
          `Ya existe una tienda con el código ${codigoEnUso.No} (${codigoEnUso.nombre || "sin nombre"})`
        );
      }
    }

    // Agregar el TPV a la base de datos
    const id = await db.tpvs.add({
      nombre: tpv.nombre,
      direccion: tpv.direccion || "",
      direccion2: tpv.direccion2 || "",
      ciudad: tpv.ciudad || "",
      codigoPostal: tpv.codigoPostal || "",
      provincia: tpv.provincia || "",
      pais: tpv.pais || "",
      telefono: tpv.telefono || "",
      movil: tpv.movil || "",
      email: tpv.email || "",
      sitioWeb: tpv.sitioWeb || "",
      nifCif: tpv.nifCif || "",
      contacto: tpv.contacto || "",
      notas: tpv.notas || "",
      fechaAlta: tpv.fechaAlta || new Date(),
      locationCode: tpv.locationCode || "",
      noSeries: tpv.noSeries || "",
      margenCierre: tpv.margenCierre || 0,
      cod_Vendedor: tpv.cod_Vendedor,
      createdAt: new Date(),
      pendingSync: 1, // Marcar como pendiente de sincronización (usando 1 en lugar de true)
      lastModified: new Date(),
      No: codigo || null, // Código de BC ya normalizado, o null para que lo asigne
      deleted: false,
    });

    return { id, ...tpv };
  } catch (error) {
    console.error("Error al agregar TPV:", error);
    throw error;
  }
};

// Obtener todos los TPVs
export const getAllTPVs = async () => {
  try {
    // Obtener solo TPVs no marcados como eliminados
    const tpvs = await db.tpvs.filter((tpv) => !tpv.deleted).toArray();
    return tpvs;
  } catch (error) {
    console.error("Error al obtener TPVs:", error);
    throw error;
  }
};

// Obtener un TPV por ID local
export const getTPVById = async (id) => {
  try {
    const tpv = await db.tpvs.get(id);
    if (!tpv || tpv.deleted) {
      throw new Error("TPV no encontrado");
    }
    return tpv;
  } catch (error) {
    console.error(`Error al obtener TPV con ID ${id}:`, error);
    throw error;
  }
};

// Obtener un TPV por No de Business Central
export const getTPVByNo = async (no) => {
  try {
    const tpv = await db.tpvs
      .where("No")
      .equals(no)
      .and((tpv) => !tpv.deleted)
      .first();

    if (!tpv) {
      throw new Error("TPV no encontrado");
    }
    return tpv;
  } catch (error) {
    console.error(`Error al obtener TPV con No ${no}:`, error);
    throw error;
  }
};

// Obtiene un TPV por No o ID, busca si hay tpv con ese id, y si no lo encuentra busca por no
export const getTPVByNoOrId = async (noOrId) => {
  if (noOrId === null || noOrId === undefined) {
    return null;
  }

  try {
    let tpvInfo = await db.tpvs.get(noOrId);
    if (!tpvInfo) {
      tpvInfo = await getTPVByNo(noOrId);
    }
    return tpvInfo;
  } catch (error) {
    console.error(`Error al obtener TPV con No o ID ${noOrId}:`, error);
    throw error;
  }
};

// Actualizar un TPV existente
export const updateTPV = async (id, tpvData) => {
  try {
    const tpv = await db.tpvs.get(id);
    if (!tpv || tpv.deleted) {
      throw new Error("TPV no encontrado");
    }

    const nombreEnUso = await buscarValorEnUsoAlEditar(db.tpvs, "nombre", tpvData, tpv);

    if (nombreEnUso) {
      throw new Error(`Ya existe una tienda con ese nombre: ${nombreEnUso.nombre}`);
    }

    // El código va por su propia comparación: en las tiendas el espacio es parte
    // del código ("MALL DEL SOL"), así que no se colapsa como en los nombres.
    if (tpvData.No !== undefined && normalizarCodigoTienda(tpvData.No) !== normalizarCodigoTienda(tpv.No)) {
      const codigo = normalizarCodigoTienda(tpvData.No);
      const codigoEnUso = codigo
        ? (await db.tpvs.toArray()).find(
            (otra) => !otra.deleted && otra.id !== id && normalizarCodigoTienda(otra.No) === codigo
          )
        : null;

      if (codigoEnUso) {
        throw new Error(
          `Ya existe una tienda con el código ${codigoEnUso.No} (${codigoEnUso.nombre || "sin nombre"})`
        );
      }
    }

    // Actualizar el TPV.
    //
    // El código de Business Central no se puede perder: el formulario lo manda
    // deshabilitado cuando ya está asignado, pero si por cualquier motivo
    // llegara vacío, el spread lo dejaría en null y la tienda quedaría
    // huérfana de su clave en BC -- y con ella las cajas, los almacenes y los
    // documentos que la referencian. Una vez asignado, sólo se conserva.
    const { No: noEntrante, ...resto } = tpvData;

    await db.tpvs.update(id, {
      ...resto,
      No: tpv.No || noEntrante || null,
      lastModified: new Date(),
    });

    // Marcar como pendiente de sincronización
    await markForSync("tpvs", id);

    return await getTPVById(id);
  } catch (error) {
    console.error(`Error al actualizar TPV con ID ${id}:`, error);
    throw error;
  }
};

// Eliminar un TPV
export const deleteTPV = async (id) => {
  try {
    const tpv = await db.tpvs.get(id);
    if (!tpv) {
      throw new Error("TPV no encontrado");
    }

    // Marcar como eliminado y pendiente de sincronización
    await markForDelete("tpvs", id);

    return true;
  } catch (error) {
    console.error(`Error al eliminar TPV con ID ${id}:`, error);
    throw error;
  }
};
